// Sprint F3.1 — Reconciliação Notion x Sistema (dry-run, não aplica nada)
import { useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Statistic, Button, Input, Checkbox, Tag, Tabs, Alert, Empty, Table,
  Typography, Space, Row, Col, App as AntApp, Flex, Spin,
} from 'antd';
import {
  ReloadOutlined, ThunderboltOutlined, PlayCircleOutlined, SearchOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

interface Diff { campo: string; notion: any; sistema: any; }
interface Divergente {
  notion_page_id: string; sistema_id: string;
  parcela_titulo: string | null;
  last_edited_notion: string | null;
  atualizado_sistema: string | null;
  diffs: Diff[];
}
interface SomenteNotion {
  notion_page_id: string; parcela_titulo: string | null;
  valor_previsto: number | null; data_vencimento: string | null;
  status: string | null; last_edited_time: string | null;
}
interface SomenteSistema {
  id: string; notion_page_id: string | null;
  parcela_titulo: string | null; valor_previsto: number | null;
  data_vencimento: string | null; status: string | null;
  origem: string; criado_em: string; motivo: string;
}
interface Relatorio {
  totais: {
    notion: number; sistema: number; identicos: number; divergentes: number;
    somente_notion: number; somente_sistema: number; erros_leitura_notion: number;
  };
  ranking_campos: { campo: string; count: number }[];
  somente_notion: SomenteNotion[];
  somente_sistema: SomenteSistema[];
  divergentes: Divergente[];
  amostra_identicos: { notion_page_id: string; sistema_id: string; parcela_titulo: string }[];
  erros_leitura_notion: any[];
  gerado_em: string;
  gerado_por: string;
}

const fmt = (v: any) => {
  if (v == null) return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });
};

const LABEL_CAMPO: Record<string, string> = {
  parcela_titulo: 'Título',
  descricao: 'Descrição (curta)',
  descricao_completa: 'Descrição completa',
  valor_previsto: 'Valor previsto',
  valor_faturado: 'Valor faturado',
  valor_recebido: 'Valor recebido',
  valor_liquido_previsto: 'Valor líquido previsto',
  data_vencimento: 'Vencimento',
  nota_fiscal_numero: 'NF número',
  nota_fiscal_emissao: 'NF emissão',
  status: 'Status',
  probabilidade: 'Probabilidade',
  codigo_crea: 'Código CREA',
  email_faturamento: 'E-mail faturamento',
  prazo_dias: 'Prazo (dias)',
  rateio_setores: 'Rateio por setor',
  impostos: 'Impostos',
};

function fmtCampo(campo: string, valor: any): string {
  if (valor == null || valor === '') return '—';
  if (campo === 'rateio_setores' || campo === 'impostos') {
    if (typeof valor === 'object') {
      return Object.entries(valor)
        .map(([k, v]) => `${k}: ${typeof v === 'number' && v < 1.01 ? (v * 100).toFixed(1) + '%' : fmt(v)}`)
        .join(', ');
    }
    return String(valor);
  }
  if (campo.startsWith('valor_')) return fmt(valor);
  if (campo === 'data_vencimento' || campo === 'nota_fiscal_emissao') {
    const s = String(valor).slice(0, 10);
    return fmtDataBr(s) || s;
  }
  if (typeof valor === 'string' && valor.length > 80) return valor.slice(0, 80) + '…';
  return String(valor);
}

function FinanceiroReconcileNotionInner() {
  const { modal, message } = AntApp.useApp();
  const [rel, setRel] = useState<Relatorio | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tab, setTab] = useState<'divergentes' | 'somente_notion' | 'somente_sistema' | 'identicos'>('divergentes');
  const [ocultarTriviais, setOcultarTriviais] = useState(true);
  const [filtroCampo, setFiltroCampo] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [aplicando, setAplicando] = useState(false);
  const [resultadoSync, setResultadoSync] = useState<any>(null);

  async function gerar() {
    setLoading(true); setErro(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_reconcile_notion_contas_receber',
        limite: 2000,
      }, { headers: { Authorization: token }, timeout: 300_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) { setErro(d.error); setRel(null); }
      else setRel(d);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  function aplicarSync() {
    const total = rel?.totais.divergentes ?? 0;
    const soNotion = rel?.totais.somente_notion ?? 0;
    let confirmText = '';
    modal.confirm({
      title: 'Sincronizar Notion → Sistema',
      icon: <ExclamationCircleOutlined style={{ color: '#B07500' }} />,
      width: 540,
      okText: 'Sincronizar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      content: (
        <div style={{ fontSize: 13 }}>
          <Alert
            type="warning" showIcon
            message={`Isso vai SOBRESCREVER ${total} parcelas com valores do Notion e CRIAR ${soNotion} parcelas novas.`}
            description="Parcelas existentes no sistema mas sem correspondência no Notion (origem='sistema') NÃO são afetadas."
            style={{ marginBottom: 12 }}
          />
          <div style={{ marginBottom: 6 }}>Pra confirmar, digite <strong>SINCRONIZAR</strong> abaixo:</div>
          <Input
            placeholder="SINCRONIZAR"
            onChange={(e) => { confirmText = e.target.value; }}
            autoFocus
          />
        </div>
      ),
      onOk: async () => {
        if (confirmText !== 'SINCRONIZAR') {
          message.error('Texto não confere. Sync cancelado.');
          throw new Error('cancelado');
        }
        setAplicando(true); setErro(null); setResultadoSync(null);
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'fin_sync_notion_contas_receber',
          }, { headers: { Authorization: token }, timeout: 30_000 });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          if (d.error) setErro(d.error);
          else {
            setResultadoSync(d);
            setTimeout(() => { gerar(); }, 90_000);
          }
        } catch (e: any) {
          setErro(e.response?.data?.error || e.message);
        } finally { setAplicando(false); }
      },
    });
  }

  const divergentesFiltrados = (rel?.divergentes || []).filter(d => {
    if (ocultarTriviais) {
      const trivial = d.diffs.length === 1
        && d.diffs[0].campo === 'valor_recebido'
        && d.diffs[0].notion == null;
      if (trivial) return false;
    }
    if (filtroCampo && !d.diffs.some(x => x.campo === filtroCampo)) return false;
    if (busca) {
      const q = busca.toLowerCase();
      if (!(d.parcela_titulo || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const somenteNotionFiltrado = (rel?.somente_notion || []).filter(x => {
    if (!busca) return true;
    return (x.parcela_titulo || '').toLowerCase().includes(busca.toLowerCase());
  });
  const somenteSistemaFiltrado = (rel?.somente_sistema || []).filter(x => {
    if (!busca) return true;
    return (x.parcela_titulo || '').toLowerCase().includes(busca.toLowerCase());
  });

  const colsSomenteNotion: ColumnsType<SomenteNotion> = [
    { title: 'Título', dataIndex: 'parcela_titulo', key: 't', render: (v) => v || <Text type="secondary">(sem título)</Text> },
    { title: 'Valor', dataIndex: 'valor_previsto', key: 'v', align: 'right', width: 140, render: (v) => fmt(v) },
    { title: 'Vencimento', dataIndex: 'data_vencimento', key: 'dv', width: 110, render: (v) => v ? fmtDataBr(v) : '—' },
    { title: 'Status', dataIndex: 'status', key: 's', width: 120, render: (v) => v || '—' },
    {
      title: 'Notion editado', dataIndex: 'last_edited_time', key: 'le', width: 130,
      render: (v) => v ? <Text type="secondary" style={{ fontSize: 11 }}>{fmtDataBr(v.slice(0, 10))}</Text> : '—',
    },
  ];

  const colsSomenteSistema: ColumnsType<SomenteSistema> = [
    { title: 'Título', dataIndex: 'parcela_titulo', key: 't', render: (v) => v || <Text type="secondary">(sem título)</Text> },
    { title: 'Valor', dataIndex: 'valor_previsto', key: 'v', align: 'right', width: 140, render: (v) => fmt(v) },
    { title: 'Vencimento', dataIndex: 'data_vencimento', key: 'dv', width: 110, render: (v) => v ? fmtDataBr(v) : '—' },
    { title: 'Status', dataIndex: 'status', key: 's', width: 120, render: (v) => v || '—' },
    {
      title: 'Origem', dataIndex: 'origem', key: 'o', width: 90,
      render: (v) => <Tag color={v === 'notion' ? 'blue' : 'default'}>{v}</Tag>,
    },
    { title: 'Motivo', dataIndex: 'motivo', key: 'm', render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text> },
  ];

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }}
        message="Console de emergência — o Notion é histórico"
        description="Desde 13/07/2026 o sistema é a fonte de verdade do Contas a Receber e nenhum sync lê o Notion. Esta tela consulta o Notion ao vivo só pra conferência pontual; nada é aplicado automaticamente." />
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Reconciliação Notion ↔ Sistema</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Compara <strong>Contas a Receber Hydrodata</strong> (Notion) com{' '}
            <strong>financeiro.contas_a_receber</strong> (sistema). <em>Não aplica nada</em> — apenas mostra as diferenças.
          </Text>
        </div>
        <Space>
          <Button
            type="primary"
            icon={rel ? <ReloadOutlined /> : <PlayCircleOutlined />}
            loading={loading}
            disabled={aplicando}
            onClick={gerar}
          >
            {loading ? 'Carregando…' : rel ? 'Atualizar relatório' : 'Gerar relatório'}
          </Button>
          {rel && (
            <Button
              danger
              icon={<ThunderboltOutlined />}
              loading={aplicando}
              disabled={loading}
              onClick={aplicarSync}
              title="Sobrescreve campos divergentes com valores do Notion"
            >
              Sync agora
            </Button>
          )}
        </Space>
      </Flex>

      {erro && (
        <Alert type="error" message={erro} showIcon closable onClose={() => setErro(null)} style={{ marginBottom: 16 }} />
      )}

      {resultadoSync && (
        <Alert
          type={resultadoSync.status === 'processando' ? 'warning' : 'success'}
          showIcon
          closable
          onClose={() => setResultadoSync(null)}
          message={resultadoSync.status === 'processando' ? 'Sync rodando em segundo plano' : 'Sync aplicado'}
          description={
            resultadoSync.status === 'processando' ? (
              <>
                {resultadoSync.mensagem || 'Sincronização iniciada. Pode levar 1-3 minutos.'}{' '}
                <strong>O relatório vai recarregar automaticamente em 90s.</strong>
              </>
            ) : (
              <>
                {resultadoSync.criados ?? 0} criados · {resultadoSync.atualizados ?? 0} atualizados ·
                {' '}{resultadoSync.sem_mudanca ?? 0} sem mudança · {resultadoSync.erros ?? 0} erros
                {resultadoSync.detalhes_erros?.length > 0 && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: 'pointer', color: '#B91626' }}>
                      Ver erros ({resultadoSync.detalhes_erros.length})
                    </summary>
                    <pre style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                      {JSON.stringify(resultadoSync.detalhes_erros, null, 2)}
                    </pre>
                  </details>
                )}
              </>
            )
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {!rel && !loading && !erro && (
        <Empty
          description={<>Clique em <strong>"Gerar relatório"</strong> pra puxar os dados do Notion e comparar.</>}
          style={{ padding: '40px 0' }}
        />
      )}

      {loading && (
        <Flex justify="center" style={{ padding: 60 }}>
          <Spin tip="Carregando relatório (pode levar 30-60s)…" size="large">
            <div style={{ padding: 40 }} />
          </Spin>
        </Flex>
      )}

      {rel && (
        <>
          <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
            <KpiCol label="No Notion" valor={rel.totais.notion} cor="#0930D0" />
            <KpiCol label="No Sistema" valor={rel.totais.sistema} cor="#0a7f4a" />
            <KpiCol label="Idênticos" valor={rel.totais.identicos} cor="#0a7f4a" />
            <KpiCol label="Divergentes" valor={rel.totais.divergentes} cor="#B07500" alerta />
            <KpiCol label="Só no Notion" valor={rel.totais.somente_notion} cor="#0930D0" alerta />
            <KpiCol label="Só no Sistema" valor={rel.totais.somente_sistema} cor="#666" alerta />
          </Row>

          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
            Gerado em {fmtDataBr(rel.gerado_em.slice(0, 10))} às {rel.gerado_em.slice(11, 16)} por {rel.gerado_por}.
            {rel.totais.erros_leitura_notion > 0 && (
              <Text style={{ color: '#B91626', marginLeft: 12, fontSize: 11 }}>
                ⚠ {rel.totais.erros_leitura_notion} erros na leitura do Notion
              </Text>
            )}
          </Text>

          {rel.ranking_campos.length > 0 && (
            <Card size="small" style={{ marginBottom: 12, background: '#F8F9FB' }} bodyStyle={{ padding: 10 }}>
              <Text style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 6, fontWeight: 500 }}>
                Campos que mais divergem (clique pra filtrar):
              </Text>
              <Flex wrap="wrap" gap={6}>
                <Tag.CheckableTag checked={filtroCampo === ''} onChange={() => setFiltroCampo('')}>
                  Todos
                </Tag.CheckableTag>
                {rel.ranking_campos.map(r => (
                  <Tag.CheckableTag
                    key={r.campo}
                    checked={filtroCampo === r.campo}
                    onChange={() => { setFiltroCampo(r.campo); setTab('divergentes'); }}
                  >
                    {LABEL_CAMPO[r.campo] || r.campo} ({r.count})
                  </Tag.CheckableTag>
                ))}
              </Flex>
            </Card>
          )}

          <Flex align="center" gap={14} wrap="wrap" style={{ marginBottom: 12 }}>
            <Input
              placeholder="Buscar por título da parcela…"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              prefix={<SearchOutlined style={{ color: '#aaa' }} />}
              allowClear
              style={{ maxWidth: 360, flex: 1 }}
            />
            <Checkbox checked={ocultarTriviais} onChange={e => setOcultarTriviais(e.target.checked)}>
              <Text style={{ fontSize: 12, color: '#666' }}>
                Ocultar divergências triviais (só Notion=∅, Sistema tem valor)
              </Text>
            </Checkbox>
          </Flex>

          <Tabs
            activeKey={tab}
            onChange={(k) => setTab(k as any)}
            size="small"
            items={[
              {
                key: 'divergentes',
                label: `Divergentes (${divergentesFiltrados.length}${ocultarTriviais || filtroCampo ? ` de ${rel.divergentes.length}` : ''})`,
                children: <ListaDivergentes itens={divergentesFiltrados} />,
              },
              {
                key: 'somente_notion',
                label: `Só no Notion (${somenteNotionFiltrado.length})`,
                children: somenteNotionFiltrado.length === 0
                  ? <Empty description="Nenhuma parcela só no Notion." style={{ padding: 40 }} />
                  : <Table rowKey="notion_page_id" columns={colsSomenteNotion} dataSource={somenteNotionFiltrado} size="small" pagination={{ pageSize: 50, showSizeChanger: true }} />,
              },
              {
                key: 'somente_sistema',
                label: `Só no Sistema (${somenteSistemaFiltrado.length})`,
                children: somenteSistemaFiltrado.length === 0
                  ? <Empty description="Nenhum lançamento só no sistema." style={{ padding: 40 }} />
                  : <Table rowKey="id" columns={colsSomenteSistema} dataSource={somenteSistemaFiltrado} size="small" pagination={{ pageSize: 50, showSizeChanger: true }} />,
              },
              {
                key: 'identicos',
                label: `Idênticos (amostra: ${rel.amostra_identicos.length} de ${rel.totais.identicos})`,
                children: (
                  <Card size="small" style={{ background: '#F8F9FB' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Amostra de parcelas idênticas (mesmo conteúdo no Notion e no sistema):
                    </Text>
                    <ul style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                      {rel.amostra_identicos.map(x => (
                        <li key={x.sistema_id}>
                          {x.parcela_titulo || '(sem título)'}{' '}
                          <Text type="secondary" style={{ fontSize: 10 }}>· {x.sistema_id.slice(0, 8)}</Text>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
}

function KpiCol({ label, valor, cor, alerta }: { label: string; valor: number; cor: string; alerta?: boolean }) {
  return (
    <Col xs={12} sm={8} md={4}>
      <Card
        size="small"
        bodyStyle={{ padding: '10px 12px' }}
        style={{
          borderLeft: `3px solid ${cor}`,
          borderColor: alerta && valor > 0 ? cor : undefined,
        }}
      >
        <Statistic
          title={<Text style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Text>}
          value={valor}
          valueStyle={{ color: cor, fontSize: 20, fontWeight: 600 }}
        />
      </Card>
    </Col>
  );
}

function ListaDivergentes({ itens }: { itens: Divergente[] }) {
  if (!itens.length) return <Empty description="Nenhuma divergência com os filtros atuais." style={{ padding: 40 }} />;
  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      {itens.map(d => (
        <Card key={d.sistema_id} size="small" bodyStyle={{ padding: 12 }}>
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={8} style={{ marginBottom: 8 }}>
            <div>
              <Text strong style={{ fontSize: 13 }}>{d.parcela_titulo || '(sem título)'}</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Notion: <code>{d.notion_page_id.slice(0, 8)}…</code> · Sistema: <code>{d.sistema_id.slice(0, 8)}</code>
                  {d.last_edited_notion && (
                    <span> · Notion editado {fmtDataBr(d.last_edited_notion.slice(0, 10))}</span>
                  )}
                </Text>
              </div>
            </div>
            <Tag color="gold">{d.diffs.length} {d.diffs.length === 1 ? 'campo' : 'campos'}</Tag>
          </Flex>
          <Table
            size="small"
            pagination={false}
            showHeader
            rowKey="campo"
            dataSource={d.diffs}
            columns={[
              {
                title: 'Campo', dataIndex: 'campo', key: 'c', width: '24%',
                render: (v) => <Text strong style={{ fontSize: 11 }}>{LABEL_CAMPO[v] || v}</Text>,
              },
              {
                title: 'Notion', dataIndex: 'notion', key: 'n', width: '38%',
                render: (v, r) => (
                  <Text style={{ fontSize: 11, color: v == null ? '#bbb' : '#222' }}>
                    {fmtCampo(r.campo, v)}
                  </Text>
                ),
              },
              {
                title: 'Sistema', dataIndex: 'sistema', key: 's', width: '38%',
                render: (v, r) => (
                  <Text style={{ fontSize: 11, color: v == null ? '#bbb' : '#222' }}>
                    {fmtCampo(r.campo, v)}
                  </Text>
                ),
              },
            ]}
          />
        </Card>
      ))}
    </Space>
  );
}

export default function FinanceiroReconcileNotion() {
  return (
    <AntApp>
      <FinanceiroReconcileNotionInner />
    </AntApp>
  );
}
